"use client";
import React from "react";

import { useTranslations } from "next-intl";
import toast from "react-hot-toast";

import IconOther from "@/icons/color/dots.svg";
import IconTelegram from "@/icons/color/telegram.svg";
import IconWhatsApp from "@/icons/color/whatsapp.svg";
import IconViber from "@/icons/communication/viber.svg";
import IconLink from "@/icons/link.svg";
import IconCopy from "@/icons/ui/forms/copy.svg";
import FormField from "@/ui/Common/Forms/FormField";
import { Divider } from "@/ui/Divider";
import { fallBackHandler } from "@/utils/fallback-open-messenger";
import { ModalHeader } from "@/widgets/Modal/Header";

import { ButtonComponent } from "./Button";

interface ModalShareLinkProps {
  link: string;
  title: string;
}

export const ModalShareLink: React.FC<ModalShareLinkProps> = ({
  link,
  title,
}) => {
  const t = useTranslations();

  const handleTelegramShare = () => {
    const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(title)}`;
    fallBackHandler(telegramUrl);
  };

  const handleWhatsAppShare = () => {
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`${title} ${link}`)}`;
    fallBackHandler(whatsappUrl);
  };

  const handleViberShare = () => {
    const viberUrl = `viber://forward?text=${encodeURIComponent(`${title} ${link}`)}`;
    fallBackHandler(viberUrl);
  };

  const handleOtherShare = () => {
    navigator.share({
      title: title,
      text: t("Check out this page:"),
      url: link,
    });
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(link);
    toast.success(t("Successfully copied link!"));
  };
  return (
    <div className="w-full md:w-[600px] relative flex flex-col items-stretch justify-stretch">
      <ModalHeader title={title} />
      <Divider />
      <div className={"px-12 pb-16"}>
        <p className={"my-8 text-[1.7rem] text-[#626262]"}>
          {t("Share this page with those who are interested")}
        </p>
        <FormField
          className={"mb-12"}
          readOnly
          value={link}
          insideLeftComponent={
            <IconLink
              width={18}
              height={18}
              className={"text-red"}
            />
          }
          insideRightComponent={
            <IconCopy
              width={18}
              height={18}
              className={"text-dark cursor-pointer"}
            />
          }
          onClick={handleCopyLink}
        />
        <div className={"grid grid-cols-4 gap-8 w-fit"}>
          <ButtonComponent
            onClick={handleWhatsAppShare}
            title="WhatsApp"
            icon={IconWhatsApp}
          />
          <ButtonComponent
            onClick={handleTelegramShare}
            title="Telegram"
            icon={IconTelegram}
          />
          <ButtonComponent
            onClick={handleViberShare}
            title="Viber"
            icon={IconViber}
          />
          <ButtonComponent
            onClick={handleOtherShare}
            title={t("Other")}
            icon={IconOther}
          />
        </div>
      </div>
    </div>
  );
};

export default ModalShareLink;
